/**
 * Wholesale Ordering System - Version 5.0
 * 
 * Key Features in v5.0:
 * - Individual recipient email tracking
 * - Fixed campaign dashboard data consistency
 * - Enhanced newsletter scheduling system
 * - Venues staff requirements planning
 * 
 * Updated: January 2025
 */

// src/App.js

import React, { Suspense, useEffect, useRef, useState } from 'react';
import { BrowserRouter as Router, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import Navbar from './components/Navbar';
import Loader from './components/Loader';
import ProtectedRoute from './components/ProtectedRoute';
import AnnouncementPopup from './components/AnnouncementPopup';
import AnnouncementTicker from './components/AnnouncementTicker';
import InitialPasswordReminderModal from './components/InitialPasswordReminderModal';
import AnnouncementToastManager from './components/AnnouncementToastManager';
import StockUpdateToastManager from './components/StockUpdateToastManager';
import AdminNewOrderDesktopNotifier from './components/AdminNewOrderDesktopNotifier';
import NjordSensorDesktopNotifier from './components/NjordSensorDesktopNotifier';
import AdminHamburgerNav from './components/AdminHamburgerNav';
import OperatorMessagesModal from './components/operatorMessages/OperatorMessagesModal';
import OperatorDriveModal from './components/operatorDrive/OperatorDriveModal';
import OperatorMessageAttentionPopup from './components/operatorMessages/OperatorMessageAttentionPopup';
import OperatorMessageNotifier from './components/operatorMessages/OperatorMessageNotifier';
import AppShellEffects from './components/AppShellEffects';
import { XeroInvoicesFlagsProvider } from './components/XeroInvoicesFlagsProvider';
import { BrowseChromeCompactProvider } from './context/BrowseChromeCompactContext';
import AlegartaBackground from './components/AlegartaBackground/AlegartaBackground';
import AlegartaBackgroundToggle from './components/AlegartaBackground/AlegartaBackgroundToggle';
import { OverlayManagerProvider } from './context/OverlayManagerContext';
import PrintQueueDock from './components/packingPrint/PrintQueueDock';
import BrowseCartDock from './components/browse/BrowseCartDock';
import './global.css';
import './styles/app-foundations.css';
import './styles/browse-design.css';
import './styles/customer-shell.css';
import './styles/browse-chrome-collapse.css';
import './styles/lsc-glass.css';
import './styles/toolkit-quickbar-glass.css';
import './App.css';
import logo from './images/logo.png';

// Scanner resolver (routes GTIN / DLV-… / internal batchNo)
import { scannerLookup } from './pages/ProductionToolkit/services/productionService';
import { useViewport } from './hooks/useViewport';
import {
    installKeyboardWedgeCapture,
    isScanSuspended,
    isUserTypingInField,
    scannerHiddenInputProps,
    shouldAvoidScannerInputFocus,
} from './utils/scannerFocusUtils';
import { lazyWithChunkReload } from './utils/lazyWithChunkReload';
import ErrorBoundary from './components/ErrorBoundary';
import PrintingToolKit from './components/PrintingToolKit';
import PdfEditorViewer from './pages/PdfEditorViewer';

// Lazy load all pages (auto-reload once on post-deploy ChunkLoadError).
const InvoiceControl = lazyWithChunkReload(() => import('./pages/odin/InvoiceControl'));
const Home = lazyWithChunkReload(() => import('./pages/Home'));
const Login = lazyWithChunkReload(() => import('./pages/Login'));
const Register = lazyWithChunkReload(() => import('./pages/Register'));
const ProductCatalog = lazyWithChunkReload(() => import('./pages/ProductCatalog'));
const ProductDetail = lazyWithChunkReload(() => import('./pages/ProductDetail'));
const Checkout = lazyWithChunkReload(() => import('./pages/Checkout'));
const ContactManager = lazyWithChunkReload(() => import('./pages/ContactManager'));
const EmailLogs = lazyWithChunkReload(() => import('./pages/EmailLogs'));
const AccountSettings = lazyWithChunkReload(() => import('./pages/AccountSettings'));
const AdminDashboard = lazyWithChunkReload(() => import('./pages/AdminDashboard'));
const AdminAutomation = lazyWithChunkReload(() => import('./pages/AdminAutomation'));
const AdminSegments = lazyWithChunkReload(() => import('./pages/AdminSegments'));
const AdminNjord = lazyWithChunkReload(() => import('./pages/admin/AdminNjord'));
const MessageCenter = lazyWithChunkReload(() => import('./pages/MessageCenter'));
const Cart = lazyWithChunkReload(() => import('./pages/Cart'));
const OrderHistory = lazyWithChunkReload(() => import('./pages/OrderHistory'));
const TermsAndConditions = lazyWithChunkReload(() => import('./pages/TermsAndConditions'));
const DeliveryInfo = lazyWithChunkReload(() => import('./pages/DeliveryInfo'));
const PrivacyPolicy = lazyWithChunkReload(() => import('./pages/PrivacyPolicy'));
const ContactPage = lazyWithChunkReload(() => import('./pages/ContactPage'));
const AccountsForm = lazyWithChunkReload(() => import('./pages/AccountsForm'));
const ProductManagement = lazyWithChunkReload(() => import('./pages/ProductManagement'));
const ProductDefinitionsEditor = lazyWithChunkReload(() => import('./pages/ProductDefinitionsEditor'));
const NewsletterEditor = lazyWithChunkReload(() => import('./pages/NewsletterEditor'));
const EmailTemplateStudio = lazyWithChunkReload(() => import('./pages/EmailTemplateStudio'));
const Unsubscribe = lazyWithChunkReload(() => import('./pages/Unsubscribe'));
const PublicCargoBikeTrack = lazyWithChunkReload(() => import('./pages/PublicCargoBikeTrack'));
const SensorShareView = lazyWithChunkReload(() => import('./pages/SensorShareView'));
const DriveFileSharePage = lazyWithChunkReload(() => import('./pages/DriveFileSharePage'));
import PackersRouteGate from './pages/Packers/PackersRouteGate';
const RiderToolkit = lazyWithChunkReload(() => import('./pages/RiderToolkit'));
const Odin = lazyWithChunkReload(() => import('./pages/Odin'));
const ProductGallery = lazyWithChunkReload(() => import('./pages/ProductGallery'));
// Production Toolkit entry
const ProductionRouteGate = lazyWithChunkReload(() => import('./pages/ProductionToolkit/ProductionRouteGate'));
// Xero day (finance desk; route /invoicer)
const Invoicer = lazyWithChunkReload(() => import('./pages/Invoicer'));
// Delivery Centre
const DeliveryCentre = lazyWithChunkReload(() => import('./pages/DeliveryCentre'));

// Announcement Editor (Admin only)
const AnnouncementEditor = lazyWithChunkReload(() => import('./pages/AnnouncementEditor'));

// Trade Brochure (unprotected)
const TradeBrochure = lazyWithChunkReload(() => import('./pages/TradeBrochure'));

// Venues Toolkit (Admin only)
const VenuesToolkit = lazyWithChunkReload(() => import('./pages/VenuesToolkit'));

// Staff Dashboard (Staff role)
const StaffDashboard = lazyWithChunkReload(() => import('./pages/StaffDashboard'));

// Staff Management (Admin/Manager view)
const StaffManagement = lazyWithChunkReload(() => import('./pages/StaffManagement'));

/* --------------------------
   Tiny toast for scan status
   -------------------------- */
function ScanToast({ message, onClose }) {
    if (!message) return null;
    return (
        <div
            role="status"
            aria-live="polite"
            style={{
                position: 'fixed',
                right: 12,
                bottom: 12,
                background: 'rgba(0,0,0,0.8)',
                color: '#fff',
                padding: '10px 12px',
                borderRadius: 6,
                fontSize: 13,
                zIndex: 9999,
                maxWidth: 360,
                boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
            }}
            onClick={onClose}
            title="Click to dismiss"
        >
            {message}
        </div>
    );
}

/* ---------------------------------------
   Global hidden input for BT scanner
   - Listens on Enter
   - Limited to BOH paths to avoid UX issues
   - Suspends when a form adds data-scan-suspend="true"
   - Mobile / touch: no hidden input focus; passive wedge on Production Toolkit only
   --------------------------------------- */
function GlobalScanRouter() {
    const inputRef = useRef(null);
    const [toast, setToast] = useState('');
    const navigate = useNavigate();
    const location = useLocation();

    // Only activate scanner on BOH (back-of-house) pages
    const SCAN_ACTIVE_PATHS = [/^\/ProductionToolkit/i, /^\/Packer/i, /^\/Odin/i];
    const scanActive = SCAN_ACTIVE_PATHS.some((rx) => rx.test(location.pathname));

    // Focus keeper (desktop only): one focus on route enter — no interval, no click stealing
    const { isMobile } = useViewport();
    const avoidScannerInputFocus = isMobile || shouldAvoidScannerInputFocus();

    useEffect(() => {
        if (!scanActive || avoidScannerInputFocus) return;
        const tryFocus = () => {
            if (isScanSuspended()) return;
            if (isUserTypingInField()) return;
            inputRef.current?.focus({ preventScroll: true });
        };
        const timer = window.setTimeout(tryFocus, 400);
        return () => window.clearTimeout(timer);
    }, [scanActive, avoidScannerInputFocus, location.pathname]);

    const mobileWedgeAllowed =
        avoidScannerInputFocus && /^\/ProductionToolkit/i.test(location.pathname);

    const processScanCode = React.useCallback(
        async (val) => {
            if (!scanActive || isScanSuspended() || !val) return;

            try {
                const result = await scannerLookup(val);

                if (result.type === 'gtin') {
                    if (result.sku) {
                        navigate(
                            `/ProductionToolkit?type=gtin&sku=${encodeURIComponent(result.sku)}&gtin=${encodeURIComponent(val)}&from=scan`
                        );
                        setToast(`GTIN matched → SKU ${result.sku}`);
                    } else {
                        setToast(`GTIN ${val} not mapped to a SKU.`);
                    }
                } else if (result.type === 'delivery') {
                    navigate(`/ProductionToolkit?type=delivery&id=${encodeURIComponent(result.id)}&from=scan`);
                    setToast(`Opening Delivery DLV-${result.id}`);
                } else if (result.type === 'ingredientBatch') {
                    navigate(
                        `/ProductionToolkit?type=ingredientBatch&id=${encodeURIComponent(result.id)}&from=scan`
                    );
                    setToast('Opening Ingredient Batch');
                } else if (result.type === 'productBatch') {
                    navigate(
                        `/ProductionToolkit?type=productBatch&id=${encodeURIComponent(result.id)}&from=scan`
                    );
                    setToast('Opening Product Batch');
                } else {
                    setToast(`Code not found: "${val}"`);
                }
            } catch (err) {
                // eslint-disable-next-line no-console
                console.error('Scan route error:', err);
                setToast(`Scan error: ${String(err.message || err)}`);
            } finally {
                setTimeout(() => setToast(''), 2500);
            }
        },
        [navigate, scanActive]
    );

    useEffect(() => {
        if (!mobileWedgeAllowed) return undefined;
        return installKeyboardWedgeCapture({
            enabled: () => scanActive && /^\/ProductionToolkit/i.test(location.pathname),
            onScan: processScanCode,
        });
    }, [mobileWedgeAllowed, scanActive, location.pathname, processScanCode]);

    const handleKeyDown = async (e) => {
        if (!scanActive || isScanSuspended()) return;
        if (e.key !== 'Enter') return;
        const val = e.currentTarget.value.trim();
        e.currentTarget.value = '';
        if (!val) return;
        await processScanCode(val);
        if (!avoidScannerInputFocus && inputRef.current) {
            inputRef.current.blur();
            inputRef.current.focus({ preventScroll: true });
        }
    };

    return (
        <>
            {scanActive && !avoidScannerInputFocus ? (
                <input
                    ref={inputRef}
                    type="text"
                    aria-hidden="true"
                    tabIndex={-1}
                    onKeyDown={handleKeyDown}
                    defaultValue=""
                    {...scannerHiddenInputProps()}
                    style={{
                        position: 'fixed',
                        opacity: 0,
                        width: 1,
                        height: 1,
                        left: -9999,
                        bottom: 0,
                        pointerEvents: 'none',
                    }}
                />
            ) : null}
            <ScanToast message={toast} onClose={() => setToast('')} />
        </>
    );
}
function Footer() {
    return (
        <footer className="app-footer">
            <nav>
                <ul className="footer-links">
                    <li><a href="/terms-and-conditions">Terms &amp; Conditions</a></li>
                    <li><a href="/delivery-info">Delivery Info</a></li>
                    <li><a href="/privacy-policy">Privacy Policy</a></li>
                    <li><a href="/contact">Contact</a></li>
                    <li><a href="/ProductGallery">Gallery</a></li>
                    <AlegartaBackgroundToggle />
                </ul>
            </nav>
            <p>&copy; {new Date().getFullYear()} London Smoke And Cure. All rights reserved</p>
        </footer>
    );
}

function ConditionalNavbar() {
    const location = useLocation();
    const isToolkitPage = /^\/(packer|production-toolkit)/i.test(location.pathname);
    // Public shared sensor view is a clean, consumer-facing page (no shop chrome).
    const isPublicShare = /^\/s\//i.test(location.pathname);

    // Hide navbar on toolkit pages for mobile and on public share links
    if (isToolkitPage || isPublicShare) return null;

    return (
        <>
            <Navbar />
            <AnnouncementTicker />
        </>
    );
}

function ConditionalSiteHeader() {
    const location = useLocation();
    const isPublicShare = /^\/s\//i.test(location.pathname);
    if (isPublicShare) return null;
    return (
        <header className="app-header">
            <img src={logo} alt="Company Logo" className="app-logo" />
        </header>
    );
}

function ConditionalFooter() {
    const location = useLocation();
    const isPublicShare = /^\/s\//i.test(location.pathname);
    if (isPublicShare) return null;
    return <Footer />;
}

function App() {
    return (
        <Router future={{ v7_relativeSplatPath: true, v7_startTransition: true }}>
            <BrowseChromeCompactProvider>
            <XeroInvoicesFlagsProvider>
            <AppShellEffects />
            <AlegartaBackground />
            <div className="app-site-layer">
            <ConditionalSiteHeader />
            <ConditionalNavbar />

            {/* Global scanner lives outside Suspense so it's always mounted */}
            <GlobalScanRouter />

            {/* Temporary-password reminder and mandatory first-login change gate */}
            <InitialPasswordReminderModal />

            {/* Site-wide announcement popup */}
            <AnnouncementPopup />
            
            {/* Toast notifications for staff and non-customer users */}
            <AnnouncementToastManager position="top-right" />
            
            {/* Stock update toast notifications (stocktakes and injections) */}
            <StockUpdateToastManager position="top-left" />

            {/* Admin desktop: new orders (Firestore listener; opt-in + permission in Account) */}
            <AdminNewOrderDesktopNotifier />

            {/* Njord sensor threshold alerts (RTDB live; same opt-in as desktop notifications) */}
            <NjordSensorDesktopNotifier />

            {/* Operator-to-operator attention modal mounts inside OverlayManagerProvider */}

            {/* Global Overlay Manager - Provides overlay state to all components */}
            <OverlayManagerProvider>
                {/* Global Admin Hamburger Navigation - Persists across all pages for admin users */}
                <AdminHamburgerNav />
                <OperatorMessagesModal />
                <OperatorDriveModal />
                <OperatorMessageAttentionPopup />
                <OperatorMessageNotifier />
                <PrintQueueDock />
                <BrowseCartDock />

                <ErrorBoundary context="App routes">
                <Suspense fallback={<Loader />}>
                    <Routes>
                        <Route path="/" element={<Home />} />
                        <Route path="/login" element={<Login />} />

                    {/* Admin only */}
                    <Route
                        path="/printing"
                        element={
                            <ProtectedRoute allowedRoles={['Admin']}>
                                <PrintingToolKit />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/register"
                        element={
                            <ProtectedRoute allowedRoles={['Admin']}>
                                <Register />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin"
                        element={
                            <ProtectedRoute allowedRoles={['Admin', 'ProductionAdmin', 'Production']}>
                                <AdminDashboard />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/automation"
                        element={
                            <ProtectedRoute allowedRoles={['Admin']}>
                                <AdminAutomation />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/segments"
                        element={
                            <ProtectedRoute allowedRoles={['Admin']}>
                                <AdminSegments />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/njord"
                        element={
                            <ProtectedRoute allowedRoles={['Admin']}>
                                <AdminNjord />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/ProductManagement"
                        element={
                            <ProtectedRoute allowedRoles={['Admin']}>
                                <ProductManagement />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/product-definitions"
                        element={
                            <ProtectedRoute allowedRoles={['Admin']}>
                                <ProductDefinitionsEditor />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/delivery-centre"
                        element={
                            <ProtectedRoute allowedRoles={['Admin']}>
                                <DeliveryCentre />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/NewsletterEditor"
                        element={
                            <ProtectedRoute allowedRoles={['Admin']}>
                                <NewsletterEditor />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/Packer"
                        element={
                            <ProtectedRoute allowedRoles={['Admin', 'Packer']}>
                                <PackersRouteGate />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/packer"
                        element={
                            <ProtectedRoute allowedRoles={['Admin', 'Packer']}>
                                <PackersRouteGate />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/Odin"
                        element={
                            <ProtectedRoute allowedRoles={['Admin']}>
                                <Odin />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/ProductionToolkit"
                        element={
                            <ProtectedRoute allowedRoles={['Admin', 'Production', 'ProductionAdmin']}>
                                <ProductionRouteGate />
                            </ProtectedRoute>
                        }
                    />
                    {/* Xero day */}
                    <Route
                        path="/Invoicer"
                        element={
                            <ProtectedRoute allowedRoles={['Admin']}>
                                <Invoicer />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/invoicer"
                        element={
                            <ProtectedRoute allowedRoles={['Admin']}>
                                <Invoicer />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/ContactManager"
                        element={
                            <ProtectedRoute allowedRoles={['Admin']}>
                                <ContactManager />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/email-template-studio"
                        element={
                            <ProtectedRoute allowedRoles={['Admin']}>
                                <EmailTemplateStudio />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/email-logs"
                        element={
                            <ProtectedRoute allowedRoles={['Admin']}>
                                <EmailLogs />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/announcement-editor"
                        element={
                            <ProtectedRoute allowedRoles={['Admin']}>
                                <AnnouncementEditor />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/pdf-editor"
                        element={
                            <ProtectedRoute allowedRoles={['Admin']}>
                                <React.Suspense fallback={<Loader />}>
                                    <PdfEditorViewer />
                                </React.Suspense>
                            </ProtectedRoute>
                        }
                    />

                    {/* Rider only */}
                    <Route
                        path="/rider"
                        element={
                            <ProtectedRoute allowedRoles={['Admin']}>
                                <RiderToolkit />
                            </ProtectedRoute>
                        }
                    />

                    {/* Venues Toolkit - Admin only */}
                    <Route
                        path="/venues-toolkit"
                        element={
                            <ProtectedRoute allowedRoles={['Admin']}>
                                <VenuesToolkit />
                            </ProtectedRoute>
                        }
                    />

                    {/* Staff Dashboard - Staff role */}
                    <Route
                        path="/staff-dashboard"
                        element={
                            <ProtectedRoute allowedRoles={['Staff', 'Admin']}>
                                <StaffDashboard />
                            </ProtectedRoute>
                        }
                    />

                    {/* Staff Management - Admin/Manager view */}
                    <Route
                        path="/staff-management"
                        element={
                            <ProtectedRoute allowedRoles={['Admin', 'Manager']}>
                                <StaffManagement />
                            </ProtectedRoute>
                        }
                    />

                    {/* Public / Semi-protected */}
                    <Route path="/invoice-control" element={<InvoiceControl />} />
                   
                    <Route path="/product/:id" element={<ProductDetail />} />
                    <Route path="/checkout" element={<Checkout />} />
                    <Route path="/account" element={<AccountSettings />} />
                    <Route path="/ProductGallery" element={<ProductGallery />} />
                    <Route path="/cart" element={<Cart />} />
                    <Route path="/messages" element={<MessageCenter />} />
                    <Route path="/order-history" element={<OrderHistory />} />
                    <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
                    <Route path="/delivery-info" element={<DeliveryInfo />} />
                    <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                    <Route path="/contact" element={<ContactPage />} />
                    <Route path="/AccountsForm" element={<AccountsForm />} />
                    {/* Unsubscribe page (public) */}
                    <Route path="/unsubscribe" element={<Unsubscribe />} />
                    <Route path="/track" element={<PublicCargoBikeTrack />} />
                    {/* Public read-only shared single-sensor view (no account needed) */}
                    <Route path="/s/:token" element={<SensorShareView />} />
                    <Route path="/files/internal/:token" element={<DriveFileSharePage kind="internal" />} />
                    <Route path="/files/:token" element={<DriveFileSharePage kind="external" />} />
                    {/* Trade Brochure (unprotected) */}
                    <Route path="/TradeBrochure" element={<TradeBrochure />} />
                    
                    <Route path="/products" element={<ProductCatalog />} />
                    {/* Shareable trade brochure snapshots (unprotected) */}
                    <Route path="/tradebrochure" element={<TradeBrochure />} />
                    <Route path="/tradebrochure/:code" element={<TradeBrochure />} />
                    {/* Backwards compatible uppercase variant */}
                    <Route path="/TradeBrochure/:code" element={<TradeBrochure />} />
                    </Routes>
                </Suspense>
                </ErrorBoundary>

                <ConditionalFooter />
            </OverlayManagerProvider>
            </div>
            </XeroInvoicesFlagsProvider>
            </BrowseChromeCompactProvider>
        </Router>
    );
}

export default App;
